import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
};

export function Modal({ isOpen, onClose, title, children, maxWidth = 'lg' }: ModalProps) {
  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-[9999] overflow-hidden">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start sm:items-center justify-center p-0 sm:p-2 md:p-4 mt-12  relative">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={`w-full h-full sm:h-auto ${maxWidthClasses[maxWidth]} transform relative rounded-none sm:rounded-lg md:rounded-xl bg-white p-3 sm:p-4 md:p-6 transition-all z-[9999] mx-0 sm:mx-2 overflow-y-auto sm:overflow-visible max-h-screen sm:max-h-[95vh]`}>
                <div className="flex items-center justify-between mb-3 sm:mb-4 sticky top-0 bg-white z-[9999] pb-2 border-b sm:border-b-0 sm:pb-0 sm:static">
                  <Dialog.Title className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 pr-2">
                    {title}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="rounded-full p-2 sm:p-1.5 md:p-2 hover:bg-gray-100 transition-colors duration-200 cursor-pointer flex-shrink-0"
                  >
                    <X className="w-5 h-5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-gray-500" />
                  </button>
                </div>
                <div className="pb-4 sm:pb-0">
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}